import { useState } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import AisCounter from './AisCounter';
import orange from '@material-ui/core/colors/orange';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import MissionSelect from './MissionSelect';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import Tooltip from '@material-ui/core/Tooltip';
import DateTimeRange from './DateTimeRange.js';
import PropTypes from 'prop-types';

const useStyles = makeStyles((theme) => ({
    paper: {
        marginLeft: 2,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        padding: theme.spacing(0.5),
    },
    filterSet: {
        color: orange[500]
    },
    selectFormControl: {
        margin: theme.spacing(1),
        minWidth: 90,
    },
    retentionFormControl: {
        margin: theme.spacing(1),
        width: 60,
    },
    trailFormControl: {
        margin: theme.spacing(1),
        width: 60,
    },
    aisTypeSelectFormControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    selectLabel: {
        fontSize: '0.8rem',
        transform: 'translate(0, 1.5px) scale(0.75)',
    },
    selectInput: {
        fontSize: '0.8rem',
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.5),
    },
    missionFormControl: {
        margin: theme.spacing(1),
        width: 300,
    },
    timeFormControl: {
        margin: theme.spacing(1),
    },
    timeControl: {
        paddingTop: theme.spacing(2),
    },
    missionSelect: {
        paddingTop: theme.spacing(0.5),
        '& .MuiAutocomplete-root': {
            margin: theme.spacing(1),
            minWidth: '200px',
            marginTop: '-14px'
        }
    },
    filterTimeSet: {
        color: orange[500]
    },
}));


const StyledToggleButtonGroup = withStyles((theme) => ({
    grouped: {
        margin: theme.spacing(0.5),
        border: 'none',
        '&:not(:first-child)': {
            borderRadius: theme.shape.borderRadius,
        },
        '&:first-child': {
            borderRadius: theme.shape.borderRadius,
        },
    },
}))(ToggleButtonGroup);



/**
 * AisTableToolbar
 * @param {Object} props
 */
const AisTableToolbar = props => {

    const { data, filter, timeControlOpened } = props;

    const [timeControlOpen, setTimeControlOpen] = useState(!!timeControlOpened);
    const [selectedTimes] = useState({ start: filter?.start, end: filter?.end });


    const handleSelection = selection => {
        if (props.onSelection)
            props.onSelection(selection);
    };


    const handleMissionSelect = mission => {

        if (mission) {
            const mi = {
                start: mission.startTime,
                end: mission.endTime,
                mission: {
                    id: mission._id,
                    name: mission.name,
                }
            }
            handleSelection(mi);
        } else {
            handleSelection({ start: undefined, end: undefined, mission: undefined });
        }
    }


    const handTimeButtonClick = () => {
        setTimeControlOpen(prev => !prev)
        props.onTimeControlOpen?.(!timeControlOpen);
    }



    const handleSelectTime = selectedTimes => {
        handleSelection({ start: selectedTimes.start, end: selectedTimes.end, mission: undefined });
    }

    const classes = useStyles();

    // Ensure filter exists to prevent errors
    const currentAisType = filter?.aisType || 'vessels';
    const currentMode = filter?.mode || 'last';
    const currentMission = filter?.mission;
    const timeFilter = filter?.start || filter?.end;

    return (
        <div>
            <Grid container alignItems="center">
                <Hidden only={['xs', 'sm']}>
                    <Grid item >
                        <AisCounter title={currentAisType === 'vessels' ? 'Vessels' : 'Base Stations'} data={data || []} />
                    </Grid>
                </Hidden>
                <Hidden only={['xs']}>
                    <Grid item >
                        <Paper elevation={0} className={classes.paper}>
                            <FormControl variant="standard" className={classes.selectFormControl}>
                                <InputLabel id="filter-mode-select-label" className={classes.selectLabel}>Mode</InputLabel>
                                <Select
                                    labelId="filter-mode-select-label"
                                    id="filter-mode-select"
                                    value={currentMode}
                                    onChange={(event) => handleSelection({ mode: event.target.value })}
                                    label="Mode"
                                    className={classes.selectInput}
                                    margin="dense"
                                >
                                    <MenuItem value={'last'} style={{ fontSize: '0.8rem' }}>Last</MenuItem>
                                    <MenuItem value={'all'} style={{ fontSize: '0.8rem' }}>All</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl variant="standard" className={classes.aisTypeSelectFormControl}>
                                <InputLabel id="ais-type-select-label" className={classes.selectLabel}>Type</InputLabel>
                                <Select
                                    labelId="ais-type-select-label"
                                    id="ais-type-select"
                                    value={currentAisType}
                                    onChange={(event) => handleSelection({ aisType: event.target.value })}
                                    label="Type"
                                    className={classes.selectInput}
                                    margin="dense"
                                >
                                    <MenuItem value={'vessels'} style={{ fontSize: '0.8rem' }}>Vessels</MenuItem>
                                    <MenuItem value={'aisBaseStations'} style={{ fontSize: '0.8rem' }}>Base Stations</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl variant="standard" className={classes.timeFormControl}>
                                <StyledToggleButtonGroup
                                    size="small"
                                    exclusive
                                    className={classes.timeControl}
                                    onChange={handTimeButtonClick}
                                >
                                    <ToggleButton value='time' selected={timeControlOpen} >
                                        <Tooltip title="Time">
                                            <AccessTimeIcon className={timeFilter && classes.filterTimeSet} />
                                        </Tooltip>
                                    </ToggleButton>
                                </StyledToggleButtonGroup>
                            </FormControl>
                            <FormControl variant="standard" className={classes.retentionFormControl}>
                                <InputLabel htmlFor="retention-input" className={classes.selectLabel}>Retention</InputLabel>
                                <input
                                    id="retention-input"
                                    type="number"
                                    min="1"
                                    value={filter?.retention || 1}
                                    onChange={(event) => handleSelection({ retention: Math.max(1, parseInt(event.target.value, 10) || 1) })}
                                    className={classes.selectInput}
                                    style={{ marginTop: '18px', padding: '4px', fontSize: '0.8rem' }}
                                />
                            </FormControl>
                            <FormControl variant="standard" className={classes.trailFormControl}>
                                <InputLabel htmlFor="trail-input" className={classes.selectLabel}>Trail</InputLabel>
                                <input
                                    id="trail-input"
                                    type="number"
                                    min="1"
                                    value={filter?.trail || 1}
                                    onChange={(event) => handleSelection({ trail: Math.max(1, parseInt(event.target.value, 10) || 1) })}
                                    style={{ marginTop: '18px', padding: '4px', fontSize: '0.8rem' }}
                                />
                            </FormControl>

                             {/* TODO: Temporarely removed. May bring it back */}
                            <FormControl variant="standard" className={classes.missionFormControl}>
                                <MissionSelect className={classes.missionSelect} mission={currentMission} onChange={handleMissionSelect} />
                            </FormControl>
                        </Paper>
                    </Grid>
                    {
                        timeControlOpen &&
                        <div style={{ padding: '0px 10px' }}>
                            <DateTimeRange selectTime={selectedTimes} onTimeSelected={handleSelectTime} />
                        </div>
                    }

                </Hidden>
            </Grid>
        </div>
    );
}

AisTableToolbar.propTypes = {
    data: PropTypes.array,
    filter: PropTypes.shape({
        aisType: PropTypes.string,
        mode: PropTypes.string,
        retention: PropTypes.number,
        trail: PropTypes.number,
        mission: PropTypes.string,
        start: PropTypes.number,
        end: PropTypes.number
    }),
    timeControlOpen: PropTypes.bool,
    onSelection: PropTypes.func,
    onTimeControlOpen: PropTypes.func,
};

export default AisTableToolbar;
